import { getYear, sub } from 'date-fns';
import { isEmpty } from 'lodash';
import { node } from 'prop-types';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Card,
  ConsumptionCard,
  DashboardContext,
  FiscalCard,
  FiscalModal,
  Icon,
  ReactTable,
  RightHolderModal,
  SanitizedHtml,
  Skeleton,
  StrapiImg
} from 'components';
import { getBadgeColor } from 'helpers';
import { useFetch, useSafeState, useUser } from 'hooks';
import { appService } from 'services';

export const BenefitsContext = createContext();
export const BenefitsContextProvider = ({ children }) => {
  const [rightHolder, setRightHolder] = useSafeState(null);
  const [taxeMinusTwoYear, setTaxeMinusTwoYear] = useSafeState(null);
  const { call: rightHoldersCall, loading: rightHoldersLoading, data, setData } = useFetch();
  const { call: taxesCall, loading: taxesLoading } = useFetch({ withSetState: false });

  const { currentUser } = useUser();

  useEffect(() => {
    if (currentUser?.serialNumber && appService.isTouloisirs()) {
      getRightHolders();
      getTaxes();
    }
  }, [currentUser]);

  const getRightHolders = () => {
    rightHoldersCall({
      service: 'castelis',
      path: `users/rightHolders/${currentUser.serialNumber}`,
      method: 'get'
    });
  };

  const getTaxes = async () => {
    const { data } =
      (await taxesCall({
        service: 'castelis',
        path: `users/taxeData/${currentUser.serialNumber}`,
        method: 'get'
      })) || {};

    if (!isEmpty(data?.body)) {
      const minusTwoYears = getYear(sub(new Date(), { years: 2 }));
      const taxe = data?.body?.taxeData?.find((taxe) => taxe.year === minusTwoYears);
      taxe && setTaxeMinusTwoYear(taxe);
    }
  };

  return (
    <BenefitsContext.Provider
      value={{
        taxeMinusTwoYear,
        setTaxeMinusTwoYear,
        taxesLoading,
        rightHolder,
        setRightHolder,
        rightHoldersData: data,
        setRightHolders: setData,
        rightHoldersLoading,
        getTaxes
      }}
    >
      {children}
    </BenefitsContext.Provider>
  );
};

const DashboardBenefits = () => {
  const { setRightHolder, rightHoldersData, rightHoldersLoading } = useContext(BenefitsContext);
  const { accountLoading, getAccountPage, accountData, consumptionsData, consumptionsLoading, getConsumption } =
    useContext(DashboardContext);

  const { isLaposteEmployee, currentUser } = useUser();

  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useSafeState(false);
  const [modalMode, setModalMode] = useSafeState('');
  const [isFiscalModalOpen, setIsFiscalModalOpen] = useSafeState(false);

  const rightHoldersColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: t('dashboard:table.header.lastname'),
        cell: (cell) => <span>{cell.row.original.name}</span>
      },
      {
        accessorKey: 'firstName',
        header: t('dashboard:table.header.firstname'),
        cell: (cell) => <span>{cell.row.original.firstName}</span>
      },
      {
        accessorKey: 'birthDate',
        header: t('dashboard:table.header.birthdate'),
        enableSorting: false,
        cell: (cell) => <span>{cell.row.original.birthDate}</span>
      },
      {
        accessorKey: 'address',
        header: t('dashboard:table.header.address'),
        enableSorting: false,
        cell: (cell) => {
          const { address, address1, postalCode, city } = cell.row.original;
          return <div className="w-58 break-words">{`${address ?? address1}, ${postalCode}, ${city}`}</div>;
        }
      },
      {
        accessorKey: 'linkType',
        header: t('dashboard:table.header.linkType'),
        enableSorting: false,
        cell: (cell) => <span>{cell.row.original.linkType}</span>
      },
      {
        accessorKey: 'status',
        header: t('dashboard:table.header.status'),
        cell: (cell) => {
          return <Badge color={getBadgeColor(cell.getValue())}>{cell.getValue()}</Badge>;
        }
      },

      {
        accessorKey: 'action',
        header: t('dashboard:table.header.action'),
        enableSorting: false,
        cell: (cell) => {
          return (
            <div className="flex items-center gap-4">
              <button type="button" className="btn action" onClick={() => getRightHolder(cell.row.original, 'edit')}>
                <Icon name="pen" />
              </button>
              <button type="button" className="btn action" onClick={() => getRightHolder(cell.row.original, 'delete')}>
                <Icon name="trash-alt" />
              </button>
            </div>
          );
        }
      }
    ],
    []
  );
  const benefits = useMemo(() => accountData?.data?.[0]?.benefits, [accountData]);

  useEffect(() => {
    getAccountPage('benefits');
  }, []);

  useEffect(() => {
    if (currentUser?.serialNumber) {
      getConsumption();
    }
  }, [currentUser]);

  const getRightHolder = (rightHolder, mode) => {
    setRightHolder(rightHolder);
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const openAddModal = () => {
    setRightHolder(null);
    setModalMode('add');
    setIsModalOpen(true);
  };

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-12 lg:gap-4">
      <div className="lg:col-span-9">
        {accountLoading ? (
          <Skeleton classNames="h-96 w-full rounded-xl" />
        ) : (
          <Card onMobile={false}>
            {accountData?.data?.[0]?.title && (
              <h2 className="h2 underlined-yellow mb-8"> {accountData?.data?.[0]?.title}</h2>
            )}
            <SanitizedHtml content={accountData?.data?.[0]?.intro} />

            {benefits?.map((benefit, index) => (
              <div className="flex flex-row mt-8 lg:gap-4 " key={`benefit-${index}`}>
                <div className="rounded-lg hidden shrink-0 overflow-hidden lg:w-56 lg:h-40 lg:block">
                  <StrapiImg image={benefit?.thumbnail} className="w-full h-full object-cover" />
                </div>

                <div className="flex flex-col justify-between gap-2">
                  <div>
                    {benefit?.title && (
                      <p className="h3 underlined-yellow mb-8">
                        <span className="truncate-line one-line">{benefit?.title}</span>
                      </p>
                    )}
                    <SanitizedHtml content={benefit?.content} />
                  </div>

                  {benefit?.cta?.buttonText && (
                    <a href={benefit?.cta?.link} className="btn w-fit small text-sm lg:text-base">
                      {benefit?.cta?.buttonText}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </Card>
        )}

        {isLaposteEmployee && appService.isTouloisirs() && (
          <>
            {rightHoldersLoading ? (
              <Skeleton classNames="mt-8 rounded-xl h-96 w-full" />
            ) : (
              <Card onMobile={false} className="mt-8">
                <h2 className="h2 underlined-yellow mb-8"> {t('dashboard:benefits.rightHolders')}</h2>

                {!isEmpty(rightHoldersData?.rightHolders) && (
                  <ReactTable
                    columns={rightHoldersColumns}
                    data={rightHoldersData.rightHolders}
                    pagination={rightHoldersData.rightHolders.length > 10}
                  />
                )}
                <button type="button" className="btn mt-8 ml-auto lg:ml-initial" onClick={() => openAddModal()}>
                  {t('buttons.addRightHolder')}
                </button>
              </Card>
            )}
          </>
        )}
      </div>

      <div className="order-first mt-4 lg:col-span-3 lg:order-none lg:mt-0 mb-8">
        {consumptionsLoading && (
          <>
            {[...Array(3)].map((_, index) => (
              <div className="rounded-xl h-44 overflow-hidden mb-8 lg:col-span-4" key={`consumption-skeleton-${index}`}>
                <Skeleton type="text" classNames="h-full w-full" />
              </div>
            ))}
          </>
        )}
        {!consumptionsLoading && consumptionsData && (
          <>
            {consumptionsData.map((consumption, index) => (
              <div className="mb-8 lg:col-span-4" key={`consumption-${index}`}>
                <ConsumptionCard consumption={consumption} index={index} />
              </div>
            ))}
          </>
        )}
        {isLaposteEmployee && appService.isTouloisirs() && <FiscalCard setIsFiscalModalOpen={setIsFiscalModalOpen} />}
      </div>
      {appService.isTouloisirs() && (
        <>
          <RightHolderModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} mode={modalMode} />
          <FiscalModal isModalOpen={isFiscalModalOpen} setIsModalOpen={setIsFiscalModalOpen} />
        </>
      )}
    </div>
  );
};

BenefitsContextProvider.propTypes = {
  children: node.isRequired
};

export default DashboardBenefits;
